import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Languages,
  Landmark,
  Plug,
  Home as HomeIcon,
  GraduationCap,
  CalendarCheck,
  ShieldAlert,
  ShieldCheck,
  Clock,
  Lock,
} from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Motion";
import SectionHeader from "@/components/SectionHeader";
import BookButton from "@/components/BookButton";
import { useLang } from "@/i18n";
import { SEO } from "@/components/SEO";

const SVC_ICONS = [Languages, Landmark, Plug, HomeIcon, GraduationCap, CalendarCheck];

const PageHeader = () => {
  const { t } = useLang();
  return (
    <section data-testid="personal-header" className="bg-obsidian pb-24 pt-40">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <MaskedLine delay={0.1}>
          <span className="overline text-gold">{t("personal.overline")}</span>
        </MaskedLine>
        <h1 className="mt-6 max-w-4xl font-serif text-4xl font-light leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.25}>{t("personal.line1")}</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="italic text-gold">{t("personal.line2")}</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.6}>
          <span className="mt-8 block max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
            {t("personal.intro")}
          </span>
        </MaskedLine>
        <MaskedLine delay={0.75}>
          <span data-testid="personal-languages" className="mt-8 block font-mono text-sm uppercase tracking-[0.25em] text-gold">
            {t("personal.languages")}
          </span>
        </MaskedLine>
        <MaskedLine delay={0.85}>
          <span data-testid="personal-worldwide" className="mt-3 block font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
            {t("personal.worldwide")}
          </span>
        </MaskedLine>
      </div>
    </section>
  );
};

const WhoThisIsFor = () => {
  const { t } = useLang();
  return (
    <section data-testid="personal-who-section" className="bg-alabaster py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHeader center testid="personal-who" overline={t("personal.whoOverline")} title={t("personal.whoTitle")} />
        <Reveal delay={0.15} className="mt-14 flex flex-wrap justify-center gap-4">
          {t("personal.whoList").map((type, i) => (
            <span
              key={i}
              data-testid={`personal-who-chip-${i + 1}`}
              className="rounded-full border border-obsidian/15 bg-white px-6 py-3 text-sm text-obsidian transition-colors duration-300 hover:border-gold hover:text-gold-muted"
            >
              {type}
            </span>
          ))}
        </Reveal>
      </div>
    </section>
  );
};

const PersonalServices = () => {
  const { t } = useLang();
  return (
    <section data-testid="personal-services-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHeader
          testid="personal-services"
          overline={t("personal.svcOverline")}
          title={t("personal.svcTitle")}
          copy={t("personal.svcCopy")}
        />
        <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {t("personal.services").map((s, i) => {
            const Icon = SVC_ICONS[i % SVC_ICONS.length];
            return (
              <Reveal key={i} delay={(i % 3) * 0.1}>
                <div
                  data-testid={`personal-service-card-${i + 1}`}
                  className="card-lift h-full rounded-xl border border-obsidian/10 bg-alabaster p-8 hover:border-gold/50 hover:shadow-xl"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-gold/30 bg-white text-gold-muted">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-serif text-2xl leading-snug text-obsidian">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.text}</p>
                  <ul className="mt-6 space-y-2.5">
                    {s.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-muted" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Steps = () => {
  const { t } = useLang();
  return (
    <section data-testid="personal-steps-section" className="bg-obsidian py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHeader dark testid="personal-steps" overline={t("personal.stepsOverline")} title={t("personal.stepsTitle")} />
        <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {t("personal.steps").map((step, i) => (
            <Reveal key={i} delay={i * 0.12} className="relative">
              <div data-testid={`personal-step-${i + 1}`} className="border-t border-white/10 pt-8">
                <p className="font-mono text-5xl font-light text-gold/50">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-5 font-serif text-2xl text-white">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{step.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Disclaimers = () => {
  const { t } = useLang();
  return (
    <section data-testid="personal-disclaimers-section" className="bg-alabaster py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHeader
          testid="personal-disclaimers"
          overline={t("personal.disOverline")}
          title={t("personal.disTitle")}
          copy={t("personal.disIntro")}
        />
        <div className="mt-16 grid gap-6 md:grid-cols-2">
          {t("personal.disclaimers").map((d, i) => (
            <Reveal key={i} delay={(i % 2) * 0.1}>
              <div
                data-testid={`personal-disclaimer-${i + 1}`}
                className="h-full rounded-xl border border-gold/40 bg-white p-8"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-gold/30 bg-alabaster text-gold-muted">
                  <ShieldAlert className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-base font-semibold text-obsidian">{d.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{d.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const WHY_ICONS = [Languages, ShieldCheck, Clock, Lock];

const Partners = () => {
  const { t } = useLang();
  return (
    <section data-testid="personal-partners-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <p className="overline text-gold-muted">{t("personal.partnerOverline")}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl leading-[1.15] tracking-tight text-obsidian sm:text-4xl">
              {t("personal.partnerTitle")}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p data-testid="personal-partner-line" className="mx-auto mt-8 max-w-2xl font-serif text-xl italic leading-relaxed text-gold-muted sm:text-2xl">
              &ldquo;{t("personal.partnerLine")}&rdquo;
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-6 max-w-2xl text-sm leading-relaxed text-slate-500 sm:text-base">
              {t("personal.partnerText")}
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.25} className="mt-14">
          <p className="overline mb-6 text-center text-slate-400">{t("personal.partnerTypesLabel")}</p>
          <div className="flex flex-wrap justify-center gap-3">
            {t("personal.partnerTypes").map((type, i) => (
              <span
                key={i}
                data-testid={`personal-partner-type-${i + 1}`}
                className="rounded-full border border-obsidian/15 bg-alabaster px-5 py-2.5 text-sm text-obsidian transition-colors duration-300 hover:border-gold hover:text-gold-muted"
              >
                {type}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.3} className="mt-20">
          <h3 className="text-center font-serif text-2xl text-obsidian sm:text-3xl">{t("personal.partnerWhyTitle")}</h3>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {t("personal.partnerWhy").map((w, i) => {
              const Icon = WHY_ICONS[i % WHY_ICONS.length];
              return (
                <div
                  key={i}
                  data-testid={`personal-partner-why-${i + 1}`}
                  className="card-lift h-full rounded-xl border border-obsidian/10 bg-alabaster p-7 hover:border-gold/50 hover:shadow-xl"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-gold/30 bg-white text-gold-muted">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h4 className="mt-5 text-base font-semibold text-obsidian">{w.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.text}</p>
                </div>
              );
            })}
          </div>
        </Reveal>
        <Reveal delay={0.35} className="mt-12 text-center">
          <Link to="/contact?track=business" data-testid="personal-partner-cta-button" className="btn-gold">
            {t("personal.partnerCta")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const PersonalCTA = () => {
  const { t } = useLang();
  return (
    <section data-testid="personal-cta-section" className="relative overflow-hidden bg-obsidian py-24 lg:py-32">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-gold/10 blur-[120px]" />
      <div className="relative mx-auto max-w-4xl px-6 text-center sm:px-12">
        <Reveal>
          <p className="overline text-gold">{t("personal.ctaOverline")}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif text-3xl leading-[1.15] tracking-tight text-white sm:text-4xl lg:text-5xl">
            {t("personal.ctaTitle")}
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
            {t("personal.ctaText")}
          </p>
        </Reveal>
        <Reveal delay={0.2} className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <BookButton testid="personal-cta-book-button" />
          <Link to="/contact?track=personal" data-testid="personal-cta-contact-button" className="btn-outline-light">
            {t("common.quote")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

export default function PersonalSupport() {
  return (
    <div data-testid="personal-support-page">
      <SEO
        title="Personal & Newcomer Support | VCK Business Lab"
        description="Language assistance, insurance & banking communication, utilities, housing, schools, and everyday administrative support for individuals, families, and newcomers to Canada — 100% virtual, in your language."
        path="/personal-support"
      />
      <PageHeader />
      <WhoThisIsFor />
      <PersonalServices />
      <Steps />
      <Disclaimers />
      <Partners />
      <PersonalCTA />
    </div>
  );
}
